import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Add from '@mui/icons-material/Add';
import Remove from '@mui/icons-material/Remove';
import {
  Box,
  Card,
  CardMedia,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import noImage from '../assets/no-image.png';
import PageState from '../layouts/PageState';
import { useV2Common } from '../state/CommonProvider';
import Button from '../components/Button';
import Modal from '../components/Modal';
import { storageGet } from '../platform/storage';
import {
  addToCartItem,
  computeLineUnitPrice,
  getCart,
  getCartLineForProduct,
  getProductById,
  resolveCustomizationsForCart,
  setCartLineQuantity,
} from '../services/commerce';

const LOCATION_CACHE_KEY = 'v2_location';

function optionKey(opt) {
  return String(opt.id ?? opt.name ?? '');
}

function isCheckboxCustomization(c, groupKey) {
  const t = String(c?.type ?? '').toLowerCase();
  const ui = String(c?.uiType ?? '').toLowerCase();
  if (t === 'checkbox' || ui === 'checkbox') return true;
  if (c?.allowMultiple === true || c?.multiSelect === true) return true;
  const k = String(groupKey ?? '').toLowerCase();
  if (k === 'additional_customization' || k === 'additionalcustomization') return true;
  return false;
}

function buildDefaultCustomizations(p) {
  if (!p) return {};
  const defaults = {};
  for (const [key, customization] of Object.entries(p?.customizations ?? {})) {
    const options = customization?.options ?? [];
    if (isCheckboxCustomization(customization, key)) {
      const withDefault = options.filter((opt) => opt.isDefault).map(optionKey).filter(Boolean);
      defaults[key] = withDefault.length ? withDefault : [];
    } else {
      const defaultOption = options.find((opt) => opt.isDefault) ?? options[0] ?? null;
      if (defaultOption) defaults[key] = optionKey(defaultOption);
    }
  }
  return defaults;
}

function customizationOptionLabel(groupKey, opt) {
  const hidePrice = groupKey === 'piece_cut_size';
  const add = Number(opt?.additionalPrice) || 0;
  if (!hidePrice && add > 0) return `${opt.name} (+₹${add})`;
  return opt.name;
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDesktop } = useV2Common();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [customizationOpen, setCustomizationOpen] = useState(false);
  const [selectedCustomizations, setSelectedCustomizations] = useState({});
  const [adding, setAdding] = useState(false);
  const [lineQty, setLineQty] = useState(0);
  const [cartLine, setCartLine] = useState(null);
  const [cartSummary, setCartSummary] = useState({ totalAmount: 0, totalCount: 0 });

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const cachedLocation = await storageGet(LOCATION_CACHE_KEY);
      const storeId = cachedLocation?.storeId ?? null;
      const res = await getProductById({ id, storeId });
      const p = res?.data ?? null;
      setProduct(p);

      const variants = (p?.variants ?? [])
        .filter((v) => v.visibleToCustomer !== false && v.isActive !== false);
      setSelectedVariantId(variants[0]?.sku ?? null);
      setSelectedImage(0);
      setSelectedCustomizations(buildDefaultCustomizations(p));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const images = useMemo(() => {
    if (!product) return [noImage];
    const list = Array.isArray(product.images) ? product.images.filter(Boolean) : [];
    if (list.length) return list;
    if (product.image) return [product.image];
    return [noImage];
  }, [product]);

  const variants = useMemo(
    () => (product?.variants ?? []).filter((v) => v.visibleToCustomer !== false && v.isActive !== false),
    [product]
  );

  const selectedVariant = useMemo(
    () => variants.find((v) => v.sku === selectedVariantId) ?? variants[0] ?? null,
    [variants, selectedVariantId]
  );

  const customizations = useMemo(() => {
    const obj = product?.customizations ?? {};
    return Object.entries(obj);
  }, [product]);

  const resolvedCustomizations = useMemo(
    () => resolveCustomizationsForCart(product?.customizations, selectedCustomizations),
    [product?.customizations, selectedCustomizations]
  );

  const lineUnitPrice = useMemo(
    () =>
      selectedVariant ? computeLineUnitPrice(selectedVariant, resolvedCustomizations) : 0,
    [selectedVariant, resolvedCustomizations]
  );

  const variantForStock = useMemo(() => {
    if (lineQty > 0 && cartLine?.variant?.sku) {
      return variants.find((v) => v.sku === cartLine.variant.sku) ?? cartLine.variant;
    }
    return selectedVariant;
  }, [lineQty, cartLine, variants, selectedVariant]);

  /** List / catalog unit price only (variant or product); not cart line total with customizations. */
  const catalogUnitPrice = useMemo(
    () => Number(selectedVariant?.price) || Number(product?.price) || 0,
    [selectedVariant?.price, product?.price]
  );

  const refreshCartSummary = useCallback(async () => {
    const cart = await getCart();
    setCartSummary({
      totalAmount: Number(cart.totalAmount) || 0,
      totalCount: Number(cart.totalCount) || 0,
    });
  }, []);

  const applyBagLineFromStorage = useCallback(async () => {
    if (!product?.id) {
      setCartLine(null);
      setLineQty(0);
      await refreshCartSummary();
      return;
    }
    const line = await getCartLineForProduct(product.id);
    setCartLine(line);
    setLineQty(line ? Number(line.quantity) || 0 : 0);
    await refreshCartSummary();
  }, [product?.id, refreshCartSummary]);

  useEffect(() => {
    refreshCartSummary();
  }, [refreshCartSummary]);

  const handleCustomizationModalClose = useCallback(
    (_event, reason) => {
      setCustomizationOpen(false);
      const dismissed =
        reason === 'backdropClick' ||
        reason === 'escapeKeyDown' ||
        reason === undefined;
      if (dismissed && product) {
        setSelectedCustomizations(buildDefaultCustomizations(product));
      }
    },
    [product]
  );

  useEffect(() => {
    let cancelled = false;
    async function syncBag() {
      if (!product?.id) {
        if (!cancelled) {
          setCartLine(null);
          setLineQty(0);
        }
        return;
      }
      const line = await getCartLineForProduct(product.id);
      if (cancelled) return;
      setCartLine(line);
      setLineQty(line ? Number(line.quantity) || 0 : 0);
    }
    syncBag();
    return () => {
      cancelled = true;
    };
  }, [product?.id]);

  useEffect(() => {
    if (lineQty > 0 && cartLine?.variant?.sku) {
      setSelectedVariantId(cartLine.variant.sku);
    }
  }, [lineQty, cartLine?.variant?.sku]);

  const handleCustomizationSelectSingle = (groupKey, option) => {
    setSelectedCustomizations((prev) => ({ ...prev, [groupKey]: optionKey(option) }));
  };

  const handleCustomizationToggleCheckbox = (groupKey, option, checked) => {
    const id = optionKey(option);
    if (!id) return;
    setSelectedCustomizations((prev) => {
      const current = Array.isArray(prev[groupKey]) ? prev[groupKey] : [];
      const next = checked ? [...new Set([...current, id])] : current.filter((x) => x !== id);
      return { ...prev, [groupKey]: next };
    });
  };

  const handleAddToCartClick = () => {
    if (adding) return;
    if (customizations.length > 0) {
      setCustomizationOpen(true);
      return;
    }
    handleConfirmCustomizations();
  };

  const handleConfirmCustomizations = async () => {
    if (!product || !selectedVariant || adding) return;
    setAdding(true);
    try {
      await addToCartItem({
        productId: product.id,
        name: product.name,
        imageUrl: images[0] || noImage,
        variant: selectedVariant,
        customizations: resolvedCustomizations,
      });
      await applyBagLineFromStorage();
      setCustomizationOpen(false);
    } finally {
      setAdding(false);
    }
  };

  const handleDecrementQty = async () => {
    if (!product || lineQty <= 0) return;
    await setCartLineQuantity({ productId: product.id }, lineQty - 1);
    await applyBagLineFromStorage();
  };

  const handleIncrementQty = async () => {
    if (!product || adding || lineQty <= 0) return;
    await setCartLineQuantity({ productId: product.id }, lineQty + 1);
    await applyBagLineFromStorage();
  };

  return (
    <PageState loading={loading} error={error} onRetry={load} loadingMessage="Loading product...">
      {product ? (
        <Box
          sx={{
            px: { xs: 2, md: 0 },
            pt: 1,
            pb:
              cartSummary.totalCount > 0
                ? (theme) =>
                    isDesktop
                      ? theme.spacing(14)
                      : `calc(${theme.spacing(8)} + 56px + ${theme.spacing(2)} + env(safe-area-inset-bottom, 0px))`
                : 3,
          }}
        >
          <Button variant="text" size="small" onClick={() => navigate(-1)} sx={{ mb: { xs: 1, md: 1.5 } }}>
            Back
          </Button>

          <Grid container spacing={{ xs: 2, md: 4 }} alignItems="flex-start">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 1, md: 1.5 },
                  alignItems: { md: 'flex-start' },
                }}
              >
                <Box sx={{ order: { xs: 0, md: 1 }, flex: { md: 1 }, minWidth: 0, width: '100%' }}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: { md: '0 8px 32px rgba(0,0,0,0.06)' },
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={images[selectedImage] || noImage}
                      alt={product.name}
                      sx={{
                        width: '100%',
                        minHeight: { xs: 280, md: 360 },
                        maxHeight: { md: 480 },
                        objectFit: 'cover',
                        bgcolor: 'grey.100',
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = noImage;
                      }}
                    />
                  </Card>
                </Box>

                {images.length > 1 ? (
                  <Box
                    sx={{
                      order: { xs: 1, md: 0 },
                      display: 'flex',
                      flexDirection: { xs: 'row', md: 'column' },
                      gap: 1,
                      flexShrink: 0,
                      width: { xs: '100%', md: 84 },
                      maxHeight: { md: 480 },
                      overflowY: { md: 'auto' },
                      overflowX: { xs: 'auto', md: 'hidden' },
                      pb: { xs: 0.25, md: 0 },
                    }}
                  >
                    {images.map((img, idx) => (
                      <Card
                        key={`${img}-${idx}`}
                        elevation={0}
                        onClick={() => setSelectedImage(idx)}
                        sx={{
                          flex: { xs: '0 0 auto', md: '0 0 auto' },
                          width: { xs: 76, md: '100%' },
                          border: '2px solid',
                          borderColor: selectedImage === idx ? 'primary.main' : 'divider',
                          borderRadius: 1.5,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          transition: 'border-color 0.15s ease',
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={img}
                          alt={`${product.name} ${idx + 1}`}
                          sx={{
                            width: '100%',
                            height: { xs: 72, md: 70 },
                            objectFit: 'cover',
                            bgcolor: 'grey.100',
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = noImage;
                          }}
                        />
                      </Card>
                    ))}
                  </Box>
                ) : null}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: { md: 'sticky' },
                  top: { md: 72 },
                  alignSelf: { md: 'flex-start' },
                }}
              >
                <Box sx={{ mt: { xs: 0, md: 0 } }}>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{ fontSize: { md: '1.5rem' }, letterSpacing: { md: '-0.02em' } }}
                  >
                    {product.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    {product.category ? <Chip size="small" label={product.category} /> : null}
                    {!product.isStockAvailable ? (
                      <Chip size="small" color="default" label="Out of stock" />
                    ) : null}
                  </Box>
                </Box>

                {variants.length > 0 && (
                  <Box sx={{ mt: 2.5 }}>
                    {lineQty > 0 && cartLine?.variant ? (
                      <>
                        <Typography variant="subtitle2" fontWeight={700} mb={1}>
                          Size in bag
                        </Typography>
                        <Chip
                          variant="filled"
                          color="primary"
                          label={
                            cartLine.variant.weight
                              ? `${cartLine.variant.weight} · ₹${
                                  Number(cartLine.price) || Number(cartLine.variant.price) || 0
                                }`
                              : `₹${Number(cartLine.price) || Number(cartLine.variant.price) || 0}`
                          }
                          sx={{ borderRadius: 1 }}
                        />
                      </>
                    ) : (
                      <>
                        <Typography variant="subtitle2" fontWeight={700} mb={1}>
                          Choose size
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {variants.map((v) => (
                            <Chip
                              key={v.sku}
                              clickable
                              onClick={() => setSelectedVariantId(v.sku)}
                              variant={selectedVariant?.sku === v.sku ? 'filled' : 'outlined'}
                              color={selectedVariant?.sku === v.sku ? 'primary' : 'default'}
                              label={`${v.weight} • ₹${v.price}`}
                              disabled={v.isInStock === false}
                              sx={{ borderRadius: '5px' }}
                            />
                          ))}
                        </Box>
                      </>
                    )}
                  </Box>
                )}

                <Box
                  sx={{
                    mt: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    maxWidth: { md: 400 },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {lineQty > 0 && cartLine?.variant?.weight
                        ? `Size in bag · ${cartLine.variant.weight}`
                        : selectedVariant?.weight || (variants.length ? 'Select a size above' : 'Price')}
                    </Typography>
                    <Typography variant="h5" fontWeight={800} lineHeight={1.2} sx={{ mt: 0.25 }}>
                      ₹{catalogUnitPrice}
                    </Typography>
                  </Box>
                  {lineQty > 0 ? (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '52px 1fr 52px',
                        alignItems: 'center',
                        justifyItems: 'center',
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '5px',
                        px: 1.5,
                        py: 0.5,
                        minHeight: 48,
                      }}
                    >
                      <IconButton
                        size="medium"
                        aria-label="Decrease quantity"
                        onClick={handleDecrementQty}
                        disabled={lineQty <= 0}
                        sx={{ justifySelf: 'center' }}
                      >
                        <Remove fontSize="small" />
                      </IconButton>
                      <Typography variant="h6" fontWeight={800} sx={{ textAlign: 'center', width: '100%' }}>
                        {lineQty}
                      </Typography>
                      <IconButton
                        size="medium"
                        aria-label="Increase quantity"
                        onClick={handleIncrementQty}
                        disabled={
                          !variantForStock || variantForStock.isInStock === false || adding
                        }
                        sx={{ justifySelf: 'center' }}
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      fullWidth
                      size="large"
                      disabled={!selectedVariant || selectedVariant.isInStock === false || adding}
                      onClick={handleAddToCartClick}
                      sx={{
                        py: 1.5,
                        fontSize: '1.0625rem',
                        fontWeight: 700,
                        borderRadius: '5px',
                      }}
                    >
                      {adding ? 'Adding to bag...' : 'Add to bag'}
                    </Button>
                  )}
                </Box>

                {product.description ? (
                  <Box sx={{ mt: 2.5 }}>
                    <Typography variant="subtitle2" fontWeight={700} mb={1}>
                      Description
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                      {product.description}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ mt: { xs: 2.5, md: 4 } }} />

          {cartSummary.totalCount > 0 ? (
            <Box
              sx={(theme) => ({
                position: 'fixed',
                zIndex: theme.zIndex.appBar,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '5px',
                px: 2,
                py: 1.25,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                boxShadow: 3,
                ...(isDesktop
                  ? {
                      left: '50%',
                      transform: 'translateX(-50%)',
                      right: 'auto',
                      width: 'min(440px, calc(100vw - 32px))',
                      bottom: theme.spacing(2),
                    }
                  : {
                      left: theme.spacing(2),
                      right: theme.spacing(2),
                      bottom: `calc(${theme.spacing(8)} + env(safe-area-inset-bottom, 0px))`,
                    }),
              })}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={800} noWrap>
                  ₹{cartSummary.totalAmount}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  {cartSummary.totalCount} {cartSummary.totalCount === 1 ? 'item' : 'items'}
                </Typography>
              </Box>
              <Button onClick={() => navigate('/v2/cart')} sx={{ flexShrink: 0 }}>
                View cart
              </Button>
            </Box>
          ) : null}

          <Modal open={customizationOpen} onClose={handleCustomizationModalClose} title="Choose customizations">
            {customizations.map(([key, customization]) => {
              const checkbox = isCheckboxCustomization(customization, key);
              const selectedSet = checkbox
                ? new Set(Array.isArray(selectedCustomizations[key]) ? selectedCustomizations[key] : [])
                : null;

              return (
                <Box key={key} sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={700} mb={1}>
                    {customization.displayName || key}
                  </Typography>
                  {checkbox ? (
                    <FormGroup>
                      {(customization.options ?? []).map((opt) => {
                        const oid = optionKey(opt);
                        const label = customizationOptionLabel(key, opt);
                        return (
                          <FormControlLabel
                            key={oid || label}
                            control={
                              <Checkbox
                                checked={selectedSet.has(oid)}
                                onChange={(e) => handleCustomizationToggleCheckbox(key, opt, e.target.checked)}
                              />
                            }
                            label={label}
                          />
                        );
                      })}
                    </FormGroup>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {(customization.options ?? []).map((opt) => {
                        const optionId = optionKey(opt);
                        const active = selectedCustomizations[key] === optionId;
                        return (
                          <Chip
                            key={optionId || opt.name}
                            clickable
                            onClick={() => handleCustomizationSelectSingle(key, opt)}
                            color={active ? 'primary' : 'default'}
                            variant={active ? 'filled' : 'outlined'}
                            label={customizationOptionLabel(key, opt)}
                            sx={{ borderRadius: '5px' }}
                          />
                        );
                      })}
                    </Box>
                  )}
                </Box>
              );
            })}
            <Box
              sx={{
                mt: 2,
                pt: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                Total
              </Typography>
              <Typography variant="subtitle1" fontWeight={800}>
                ₹{lineUnitPrice || selectedVariant?.price || product.price || 0}
              </Typography>
            </Box>
            <Button fullWidth sx={{ mt: 2 }} onClick={handleConfirmCustomizations} disabled={adding}>
              {adding ? 'Adding to bag...' : 'Add to bag'}
            </Button>
          </Modal>
        </Box>
      ) : null}
    </PageState>
  );
}
